import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import {
  attachTodoTaskEmail,
  bulkUpdateTodoTaskStatus,
  createTodoList,
  createTodoProject,
  createTodoTag,
  createTodoTask,
  deleteTodoList,
  deleteTodoProject,
  deleteTodoTag,
  detachTodoTaskEmail,
  fetchTodoBootstrap,
  fetchTodoTask,
  purgeTodoTask,
  reorderTodoLists,
  reorderTodoProjects,
  reorderTodoTasks,
  toggleTodoTaskSomeday,
  updateTodoList,
  updateTodoProject,
  updateTodoTag,
  updateTodoTask,
  updateTodoTaskStatus,
  updateTodoTaskTags,
  type ReorderTaskInput,
} from '@/api/todoApi';
import { unlinkClickUpIntegration as apiUnlinkClickUpIntegration } from '@/api/integrationApi';
import { useTodoStore } from './todoStore';
import { useTodoViewStore } from './todoViewStore';
import { useOperationStore } from './operationStore';
import type { TodoList, TodoProject, TodoTag, TodoTask } from '@/types/todo';

interface TodoActionsStore {
  bootstrap: () => Promise<void>;
  createTask: (input: {
    title: string;
    listId?: string | null;
    projectId?: string | null;
    emails?: { accountId: string; messageId: string }[];
    scheduledDate?: string | null;
    dueDate?: string | null;
    notesMarkdown?: string;
  }) => Promise<TodoTask>;
  duplicateTask: (taskId: string) => Promise<TodoTask>;
  saveTask: (taskId: string, payload: Partial<TodoTask>) => Promise<{ task: TodoTask; spawnedTask: TodoTask | null }>;
  setTaskStatus: (taskId: string, status: TodoTask['status']) => Promise<{ task: TodoTask; spawnedTask: TodoTask | null }>;
  bulkSetTaskStatus: (
    taskIds: string[],
    status: TodoTask['status']
  ) => Promise<Array<{ task: TodoTask; spawnedTask: TodoTask | null }>>;
  linkTaskEmail: (taskId: string, accountId: string, messageId: string) => Promise<TodoTask>;
  unlinkTaskEmail: (taskId: string, accountId: string, messageId: string) => Promise<TodoTask>;
  unlinkClickUpIntegration: (taskId: string) => Promise<TodoTask>;
  createList: (payload: { name: string; icon?: string | null; color?: string | null }) => Promise<TodoList>;
  editList: (listId: string, payload: Partial<TodoList>) => Promise<TodoList>;
  destroyList: (listId: string) => Promise<{ deleted: TodoList | null; fallbackListId: string | null }>;
  reorderLists: (updates: Array<{ id: string; sortKey: number }>) => Promise<TodoList[]>;
  createProject: (payload: { listId: string; name: string; icon?: string | null; color?: string | null }) => Promise<TodoProject>;
  editProject: (projectId: string, payload: Partial<TodoProject>) => Promise<TodoProject>;
  destroyProject: (projectId: string) => Promise<TodoProject | null>;
  reorderProjects: (updates: Array<{ id: string; sortKey: number }>) => Promise<TodoProject[]>;
  createTag: (payload: { name: string; color?: string | null }) => Promise<TodoTag>;
  editTag: (tagId: string, payload: Partial<TodoTag>) => Promise<TodoTag>;
  destroyTag: (tagId: string) => Promise<TodoTag | null>;
  updateTaskTags: (taskId: string, tagIds: string[]) => Promise<TodoTask>;
  reorderTasks: (updates: ReorderTaskInput[]) => Promise<TodoTask[]>;
  toggleTaskSomeday: (taskId: string, isSomeday: boolean) => Promise<TodoTask>;
  purgeTask: (taskId: string) => Promise<TodoTask>;
  refreshTask: (taskId: string) => Promise<TodoTask>;
}

const cloneTask = (task: TodoTask): TodoTask => JSON.parse(JSON.stringify(task));

const resolveDefaultListId = (explicit?: string | null): string | null => {
  if (explicit) {
    return explicit;
  }
  const viewState = useTodoViewStore.getState();
  if (viewState.activeListId) {
    return viewState.activeListId;
  }
  const todoState = useTodoStore.getState();
  const inboxId = todoState.listOrder.find((id) => todoState.lists[id]?.isInbox);
  if (inboxId) {
    return inboxId;
  }
  return todoState.listOrder[0] ?? Object.keys(todoState.lists)[0] ?? null;
};

const getTaskSnapshot = (taskId: string): TodoTask | null => {
  const task = useTodoStore.getState().tasks[taskId];
  return task ? cloneTask(task) : null;
};

const nowIsoString = () => new Date().toISOString();

const applyLocalUpdates = (task: TodoTask, updates: Partial<TodoTask>): TodoTask => {
  const timestamp = nowIsoString();
  return {
    ...task,
    ...updates,
    updatedAt: updates.updatedAt ?? timestamp,
  };
};

const applyLocalStatus = (task: TodoTask, status: TodoTask['status']): TodoTask => {
  const timestamp = nowIsoString();
  const next: TodoTask = {
    ...task,
    status,
    updatedAt: timestamp,
  };

  if (status === 'completed') {
    next.completedAt = timestamp;
    next.cancelledAt = null;
    next.deletedAt = null;
  } else if (status === 'cancelled') {
    next.completedAt = null;
    next.cancelledAt = timestamp;
    next.deletedAt = null;
  } else if (status === 'deleted') {
    next.completedAt = null;
    next.cancelledAt = null;
    next.deletedAt = timestamp;
  } else if (status === 'open') {
    next.completedAt = null;
    next.cancelledAt = null;
    next.deletedAt = null;
  }

  return next;
};

const upsertServerTask = (task?: TodoTask | null) => {
  if (!task) {
    return;
  }
  const todoStore = useTodoStore.getState();
  const existing = todoStore.tasks[task.id];
  if (
    existing &&
    existing.updatedAt &&
    task.updatedAt &&
    existing.updatedAt.localeCompare(task.updatedAt) > 0
  ) {
    return;
  }
  todoStore.upsertTask(task);
};

const applyReorderUpdate = (task: TodoTask, update: ReorderTaskInput): TodoTask =>
  applyLocalUpdates(task, {
    position: update.position ?? task.position,
    listId: update.listId ?? task.listId,
    projectId: update.projectId === undefined ? task.projectId : update.projectId,
    scheduledDate: update.scheduledDate ?? task.scheduledDate ?? null,
    dueDate: update.dueDate ?? task.dueDate ?? null,
    isSomeday: update.isSomeday ?? task.isSomeday,
  });

export const useTodoActionsStore = create<TodoActionsStore>()(
  conditionalDevtools(
    () => ({
      bootstrap: async () => {
        const todoStore = useTodoStore.getState();
        todoStore.setLoading(true);
        todoStore.setBootstrapFailed(false);
        try {
          const payload = await fetchTodoBootstrap();
          todoStore.hydrate(payload);
        } catch (error) {
          console.error('Failed to bootstrap todo workspace', error);
          todoStore.setBootstrapFailed(true);
        } finally {
          todoStore.setLoading(false);
        }
      },

      createTask: async (input) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const listId = resolveDefaultListId(input.listId);
        if (!listId) {
          throw new Error('No list available for task creation');
        }
        const tempId = `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const now = new Date().toISOString();
        const optimisticTask: TodoTask = {
          id: tempId,
          userId: 'local',
          listId,
          projectId: input.projectId ?? null,
          title: input.title,
          notesMarkdown: input.notesMarkdown ?? '',
          status: 'open',
          priority: null,
          isSomeday: false,
          startDate: null,
          dueDate: input.dueDate ?? null,
          scheduledDate: input.scheduledDate ?? null,
          completedAt: null,
          cancelledAt: null,
          deletedAt: null,
          repeatLeadTimeDays: null,
          position: Date.now(),
          metadata: {},
          createdAt: now,
          updatedAt: now,
          tags: [],
          emails: input.emails ?? [],
        };
        todoStore.upsertTask(optimisticTask);
        operationStore.markOperationPending(tempId);
        try {
          const task = await createTodoTask({
            title: input.title,
            listId,
            projectId: input.projectId ?? undefined,
            emails: input.emails,
            scheduledDate: input.scheduledDate ?? undefined,
            dueDate: input.dueDate ?? undefined,
            notesMarkdown: input.notesMarkdown,
          });
          // Mark the real task ID as pending BEFORE modifying store to prevent
          // realtime events from adding the task a second time (race condition fix)
          operationStore.markOperationPending(task.id);
          todoStore.removeTask(tempId);
          upsertServerTask(task);
          operationStore.markOperationComplete(tempId);
          operationStore.markOperationComplete(task.id);
          return task;
        } catch (error) {
          console.error('Failed to create task', error);
          todoStore.removeTask(tempId);
          operationStore.markOperationError(tempId);
          throw error;
        }
      },

      duplicateTask: async (taskId) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const snapshot = getTaskSnapshot(taskId);
        if (!snapshot) {
          throw new Error('Task not found');
        }
        operationStore.markOperationPending(taskId);
        try {
          const task = await createTodoTask({
            title: snapshot.title,
            notesMarkdown: snapshot.notesMarkdown,
            listId: snapshot.listId,
            projectId: snapshot.projectId ?? undefined,
            priority: snapshot.priority,
            startDate: snapshot.startDate,
            scheduledDate: snapshot.scheduledDate,
            dueDate: snapshot.dueDate,
            isSomeday: snapshot.isSomeday,
            tagIds: snapshot.tags.map((tag) => tag.id),
            metadata: { ...(snapshot.metadata ?? {}) },
            repeat: snapshot.repeat ?? null,
            position: Date.now(),
            status: 'open',
          });
          todoStore.upsertTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          throw error;
        }
      },

      saveTask: async (taskId, payload) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = { ...previous, ...payload, updatedAt: new Date().toISOString() };
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const result = await updateTodoTask(taskId, payload);
          if (result.task) {
            upsertServerTask(result.task);
          }
          if (result.spawnedTask) {
            upsertServerTask(result.spawnedTask);
          }
          operationStore.markOperationComplete(taskId);
          return result;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      setTaskStatus: async (taskId, status) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = applyLocalStatus(previous, status);
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const result = await updateTodoTaskStatus(taskId, status);
          upsertServerTask(result.task);
          upsertServerTask(result.spawnedTask);
          operationStore.markOperationComplete(taskId);
          return result;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      bulkSetTaskStatus: async (taskIds, status) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const snapshots = new Map<string, TodoTask>();
        taskIds.forEach((taskId) => {
          const snapshot = getTaskSnapshot(taskId);
          if (snapshot) {
            snapshots.set(taskId, snapshot);
            todoStore.upsertTask(applyLocalStatus(snapshot, status));
            operationStore.markOperationPending(taskId);
          }
        });
        try {
          const results = await bulkUpdateTodoTaskStatus(status, taskIds);
          results.forEach((result, index) => {
            if (result.task) {
              upsertServerTask(result.task);
              operationStore.markOperationComplete(result.task.id);
            } else if (taskIds[index]) {
              operationStore.markOperationComplete(taskIds[index]);
            }
            if (result.spawnedTask) {
              upsertServerTask(result.spawnedTask);
            }
          });
          return results;
        } catch (error) {
          snapshots.forEach((snapshot, id) => {
            todoStore.upsertTask(snapshot);
            operationStore.markOperationError(id);
          });
          throw error;
        }
      },

      linkTaskEmail: async (taskId, accountId, messageId) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = applyLocalUpdates(previous, {
          emails: [...previous.emails, { accountId, messageId }],
        });
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const task = await attachTodoTaskEmail(taskId, { accountId, messageId });
          upsertServerTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      unlinkTaskEmail: async (taskId, accountId, messageId) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = applyLocalUpdates(previous, {
          emails: previous.emails.filter(
            (email) => !(email.accountId === accountId && email.messageId === messageId)
          ),
        });
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const task = await detachTodoTaskEmail(taskId, { accountId, messageId });
          upsertServerTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      unlinkClickUpIntegration: async (taskId) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = applyLocalUpdates(previous, {
          integrations: previous.integrations?.filter((i) => i.integrationType !== 'clickup') || [],
        });
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const { task } = await apiUnlinkClickUpIntegration(taskId);
          upsertServerTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      createList: async (payload) => {
        const list = await createTodoList(payload);
        useTodoStore.getState().upsertList(list);
        return list;
      },

      editList: async (listId, payload) => {
        const list = await updateTodoList(listId, payload);
        useTodoStore.getState().upsertList(list);
        return list;
      },

      destroyList: async (listId) => {
        const result = await deleteTodoList(listId);
        if (result.deleted) {
          useTodoStore.getState().removeList(result.deleted.id);
        }
        return result;
      },

      reorderLists: async (updates) => {
        const lists = await reorderTodoLists(updates);
        lists.forEach(useTodoStore.getState().upsertList);
        return lists;
      },

      createProject: async (payload) => {
        const project = await createTodoProject(payload);
        useTodoStore.getState().upsertProject(project);
        return project;
      },

      editProject: async (projectId, payload) => {
        const project = await updateTodoProject(projectId, payload);
        useTodoStore.getState().upsertProject(project);
        return project;
      },

      destroyProject: async (projectId) => {
        const project = await deleteTodoProject(projectId);
        if (project) {
          useTodoStore.getState().removeProject(project.id);
        }
        return project;
      },

      reorderProjects: async (updates) => {
        const projects = await reorderTodoProjects(updates);
        projects.forEach(useTodoStore.getState().upsertProject);
        return projects;
      },

      createTag: async (payload) => {
        const tag = await createTodoTag(payload);
        useTodoStore.getState().upsertTag(tag);
        return tag;
      },

      editTag: async (tagId, payload) => {
        const tag = await updateTodoTag(tagId, payload);
        useTodoStore.getState().upsertTag(tag);
        return tag;
      },

      destroyTag: async (tagId) => {
        const tag = await deleteTodoTag(tagId);
        if (tag) {
          useTodoStore.getState().removeTag(tag.id);
        }
        return tag;
      },

      updateTaskTags: async (taskId, tagIds) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const nextTags = tagIds
          .map((tagId) => todoStore.tags[tagId])
          .filter((tag): tag is TodoTag => Boolean(tag));
        const optimistic = applyLocalUpdates(previous, { tags: nextTags });
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const task = await updateTodoTaskTags(taskId, tagIds);
          upsertServerTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      reorderTasks: async (updates) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const snapshots = new Map<string, TodoTask>();
        updates.forEach((update) => {
          const snapshot = getTaskSnapshot(update.taskId);
          if (snapshot) {
            snapshots.set(update.taskId, snapshot);
            const optimistic = applyReorderUpdate(snapshot, update);
            todoStore.upsertTask(optimistic);
            operationStore.markOperationPending(update.taskId);
          }
        });
        try {
          const tasks = await reorderTodoTasks(updates);
          tasks.forEach((task) => {
            upsertServerTask(task);
            operationStore.markOperationComplete(task.id);
          });
          return tasks;
        } catch (error) {
          snapshots.forEach((snapshot, id) => {
            todoStore.upsertTask(snapshot);
            operationStore.markOperationError(id);
          });
          throw error;
        }
      },

      toggleTaskSomeday: async (taskId, isSomeday) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const previous = getTaskSnapshot(taskId);
        if (!previous) {
          throw new Error('Task not found');
        }
        const optimistic = applyLocalUpdates(previous, { isSomeday });
        todoStore.upsertTask(optimistic);
        operationStore.markOperationPending(taskId);
        try {
          const task = await toggleTodoTaskSomeday(taskId, isSomeday);
          upsertServerTask(task);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          operationStore.markOperationError(taskId);
          todoStore.upsertTask(previous);
          throw error;
        }
      },

      purgeTask: async (taskId) => {
        const todoStore = useTodoStore.getState();
        const operationStore = useOperationStore.getState();
        const snapshot = getTaskSnapshot(taskId);
        todoStore.removeTask(taskId);
        operationStore.markOperationPending(taskId);
        try {
          const task = await purgeTodoTask(taskId);
          operationStore.markOperationComplete(taskId);
          return task;
        } catch (error) {
          if (snapshot) {
            todoStore.upsertTask(snapshot);
          }
          operationStore.markOperationError(taskId);
          throw error;
        }
      },

      refreshTask: async (taskId) => {
        const task = await fetchTodoTask(taskId);
        useTodoStore.getState().upsertTask(task);
        return task;
      },
    }),
    { name: 'TodoActionsStore' }
  )
);
